import React from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, AlertTriangle, X, Zap, DollarSign, Activity } from 'lucide-react';

// Animated floating elements representing chaos
const floatingElements = [
  { icon: TrendingDown, color: 'text-red-500', size: 'h-6 w-6', delay: 0, x: 10, y: 15 },
  { icon: AlertTriangle, color: 'text-amber-500', size: 'h-5 w-5', delay: 0.2, x: 75, y: 25 },
  { icon: X, color: 'text-red-400', size: 'h-4 w-4', delay: 0.4, x: 25, y: 70 },
  { icon: Zap, color: 'text-amber-400', size: 'h-5 w-5', delay: 0.6, x: 85, y: 65 },
  { icon: DollarSign, color: 'text-red-500', size: 'h-4 w-4', delay: 0.8, x: 50, y: 10 },
  { icon: Activity, color: 'text-amber-500', size: 'h-6 w-6', delay: 1.0, x: 60, y: 80 },
];

// Chaotic chart line points
const chartPath = "M0,50 L10,30 L20,55 L30,20 L40,60 L50,25 L60,70 L70,35 L80,65 L90,40 L100,55";

export default function ChaoticAnimation() {
  return (
    <div 
      data-testid="chaotic-animation"
      className="relative w-full h-full min-h-[280px] rounded-2xl overflow-hidden bg-gradient-to-br from-red-100 via-slate-100 to-amber-50"
    >
      {/* Background grid pattern */}
      <div className="absolute inset-0 opacity-30">
        <svg className="w-full h-full">
          <defs>
            <pattern id="chaosGrid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(239,68,68,0.15)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#chaosGrid)" />
        </svg>
      </div>

      {/* Chaotic chart line */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        <motion.path
          d={chartPath}
          fill="none"
          stroke="rgba(220, 38, 38, 0.7)"
          strokeWidth="2"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ 
            pathLength: 1, 
            opacity: [0.4, 0.9, 0.4],
          }}
          transition={{ 
            pathLength: { duration: 2, repeat: Infinity, repeatType: 'loop' },
            opacity: { duration: 3, repeat: Infinity, repeatType: 'reverse' }
          }}
        />
      </svg>

      {/* Floating chaotic elements */}
      {floatingElements.map((el, index) => {
        const Icon = el.icon;
        return (
          <motion.div
            key={index}
            className={`absolute ${el.color}`}
            style={{ left: `${el.x}%`, top: `${el.y}%` }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ 
              opacity: [0.5, 1, 0.5],
              scale: [0.8, 1.2, 0.8],
              rotate: [0, 10, -10, 0],
              x: [0, 5, -5, 0],
              y: [0, -5, 5, 0],
            }}
            transition={{ 
              duration: 3,
              delay: el.delay,
              repeat: Infinity,
              repeatType: 'reverse',
            }}
          >
            <Icon className={el.size} />
          </motion.div>
        );
      })}

      {/* Pulsing "danger" circles */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-16 h-16 rounded-full bg-red-400/20 blur-xl"
        animate={{ scale: [1, 1.5, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 2, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-20 h-20 rounded-full bg-amber-400/20 blur-xl"
        animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.2, 0.3] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />

      {/* Content overlay - positioned to the left */}
      <div className="absolute inset-0 flex flex-col items-start justify-center p-6 pl-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-2"
        >
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <span className="text-sm font-semibold uppercase tracking-wider">Before</span>
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-slate-800">
            4 Years of Struggle
          </h3>
          <p className="text-sm text-slate-600 max-w-[180px]">
            Chasing strategies, emotional trades, and expensive lessons
          </p>
        </motion.div>

        {/* Scattered "loss" indicators */}
        <div className="absolute bottom-4 left-4 text-xs text-red-600 font-mono font-semibold">
          -$2,340
        </div>
        <div className="absolute top-4 left-1/4 text-xs text-red-600 font-mono font-semibold">
          -$890
        </div>
        <div className="absolute bottom-8 left-1/3 text-xs text-amber-600 font-mono font-semibold">
          MARGIN CALL
        </div>
      </div>
    </div>
  );
}
